import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { sendSlackMessageTracked } from "../../models/notifications";
import ModernPricingView from "../pricing/modernPricingView";

// ─── Icons ────────────────────────────────────────────────────────────────────

function RetainIcon() {
    return (
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
            <circle cx="9" cy="7" r="4"/>
            <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
            <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
        </svg>
    );
}

function TrendIcon() {
    return (
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/>
            <polyline points="17 6 23 6 23 12"/>
        </svg>
    );
}

function TagIcon() {
    return (
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/>
            <line x1="7" y1="7" x2="7.01" y2="7"/>
        </svg>
    );
}

function CreditIcon() {
    return (
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <rect x="1" y="4" width="22" height="16" rx="2" ry="2"/>
            <line x1="1" y1="10" x2="23" y2="10"/>
        </svg>
    );
}

function BellIcon() {
    return (
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
            <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
        </svg>
    );
}

function StarIcon() {
    return (
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
        </svg>
    );
}

function RefreshIcon() {
    return (
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="23 4 23 10 17 10"/>
            <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
        </svg>
    );
}

function CheckIcon() {
    return (
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
            <polyline points="20 6 9 17 4 12"/>
        </svg>
    );
}

// ─── Sections ─────────────────────────────────────────────────────────────────

function Hero({ onCTA }) {
    return (
        <section style={{ background: "#050505", padding: "100px 24px 80px", textAlign: "center" }}>
            <div style={{ maxWidth: "720px", margin: "0 auto" }}>
                <p style={{ fontSize: "11px", fontWeight: 700, color: "#22c55e", textTransform: "uppercase", letterSpacing: "0.14em", marginBottom: "18px" }}>
                    Shopify Cashback
                </p>
                <h1 style={{ fontSize: "clamp(30px, 5vw, 54px)", fontWeight: 800, color: "#fff", lineHeight: 1.1, marginBottom: "22px" }}>
                    Turn first-time buyers into repeat customers
                </h1>
                <p style={{ fontSize: "17px", color: "#9ca3af", lineHeight: 1.65, maxWidth: "580px", margin: "0 auto 36px" }}>
                    72% of first-time Shopify buyers never place a second order. Pabloo gives every customer a reason to come back by issuing store credit automatically after each purchase.
                </p>
                <div style={{ display: "flex", gap: "12px", justifyContent: "center", flexWrap: "wrap" }}>
                    <button
                        onClick={onCTA}
                        style={{ padding: "14px 28px", background: "#22c55e", color: "#fff", border: "none", borderRadius: "10px", fontSize: "14px", fontWeight: 700, cursor: "pointer" }}
                    >
                        Start free trial
                    </button>
                    <a
                        href="/pricing"
                        style={{ padding: "14px 28px", background: "transparent", color: "#e5e7eb", border: "1px solid #2a2a2a", borderRadius: "10px", fontSize: "14px", fontWeight: 600, textDecoration: "none" }}
                    >
                        See pricing
                    </a>
                </div>
            </div>
        </section>
    );
}

function StatsStrip() {
    const stats = [
        { value: "72%", label: "of first-time buyers never place a second order" },
        { value: "5x", label: "cheaper to retain a customer than acquire a new one" },
        { value: "30%", label: "increase in repeat purchases with a cashback program" },
        { value: "20%", label: "average increase in order value when customers return to spend credit" },
    ];
    return (
        <section style={{ background: "#111", borderTop: "1px solid #1a1a1a", borderBottom: "1px solid #1a1a1a", padding: "40px 24px" }}>
            <div style={{ maxWidth: "960px", margin: "0 auto", display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "32px", textAlign: "center" }}>
                {stats.map((s) => (
                    <div key={s.label}>
                        <div style={{ fontSize: "28px", fontWeight: 800, color: "#22c55e", marginBottom: "6px" }}>{s.value}</div>
                        <div style={{ fontSize: "12.5px", color: "#6b7280", lineHeight: 1.5 }}>{s.label}</div>
                    </div>
                ))}
            </div>
        </section>
    );
}

function ProblemsSection() {
    const problems = [
        {
            icon: <RetainIcon />,
            title: "Customers don't come back",
            body: "Most Shopify stores are built for acquisition, not retention. There's no automatic trigger that brings a customer back after their first purchase. They buy once and move on.",
        },
        {
            icon: <TagIcon />,
            title: "Discounts are eroding margins",
            body: "Running sales and promo codes to drive repeat orders trains customers to wait for the next deal. Each discount cohort needs deeper discounts to reactivate. Margins shrink with every cycle.",
        },
        {
            icon: <TrendIcon />,
            title: "Acquisition costs keep rising",
            body: "Ad CPMs are up. Organic reach is down. When the majority of customers only buy once, the only way to grow revenue is to spend more on ads, which gets harder every quarter.",
        },
    ];
    return (
        <section style={{ background: "#0a0a0a", padding: "80px 24px" }}>
            <div style={{ maxWidth: "900px", margin: "0 auto" }}>
                <div style={{ textAlign: "center", marginBottom: "56px" }}>
                    <p style={{ fontSize: "11px", fontWeight: 700, color: "#22c55e", textTransform: "uppercase", letterSpacing: "0.12em", marginBottom: "10px" }}>
                        The Problem
                    </p>
                    <h2 style={{ fontSize: "clamp(24px, 4vw, 36px)", fontWeight: 800, color: "#fff", lineHeight: 1.2 }}>
                        Most Shopify stores are losing money on every one-time buyer
                    </h2>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: "20px" }}>
                    {problems.map((p) => (
                        <div key={p.title} style={{ background: "#111", border: "1px solid #1e1e1e", borderRadius: "16px", padding: "28px" }}>
                            <div style={{ marginBottom: "16px" }}>{p.icon}</div>
                            <h3 style={{ fontSize: "16px", fontWeight: 700, color: "#fff", marginBottom: "10px" }}>{p.title}</h3>
                            <p style={{ fontSize: "13.5px", color: "#9ca3af", lineHeight: 1.65 }}>{p.body}</p>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

function HowCashbackWorksSection() {
    const steps = [
        {
            num: "01",
            title: "Customer places an order",
            body: "A shopper checks out on your Shopify store. No change to the checkout experience. They pay the normal price.",
        },
        {
            num: "02",
            title: "Pabloo issues store credit automatically",
            body: "Based on rules you set — a percentage of the order, a flat amount, or tied to specific products — Pabloo credits the customer's account immediately after the order is confirmed.",
        },
        {
            num: "03",
            title: "Customer gets notified",
            body: "Pabloo sends an email notification telling the customer how much credit they've earned and that it's waiting on their account.",
        },
        {
            num: "04",
            title: "Customer returns to spend it",
            body: "Their credit balance is visible at checkout and applies automatically. They come back to use it and usually spend more than the credit amount in the same order.",
        },
    ];
    return (
        <section style={{ background: "#050505", padding: "80px 24px" }}>
            <div style={{ maxWidth: "860px", margin: "0 auto" }}>
                <div style={{ textAlign: "center", marginBottom: "56px" }}>
                    <p style={{ fontSize: "11px", fontWeight: 700, color: "#22c55e", textTransform: "uppercase", letterSpacing: "0.12em", marginBottom: "10px" }}>
                        How It Works
                    </p>
                    <h2 style={{ fontSize: "clamp(24px, 4vw, 36px)", fontWeight: 800, color: "#fff", lineHeight: 1.2 }}>
                        Cashback runs automatically after every order
                    </h2>
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                    {steps.map((step) => (
                        <div key={step.num} style={{ display: "flex", gap: "24px", alignItems: "flex-start", background: "#111", border: "1px solid #1e1e1e", borderRadius: "14px", padding: "24px 28px" }}>
                            <div style={{ fontSize: "22px", fontWeight: 800, color: "#22c55e", minWidth: "36px", lineHeight: 1 }}>{step.num}</div>
                            <div>
                                <h3 style={{ fontSize: "15px", fontWeight: 700, color: "#fff", marginBottom: "6px" }}>{step.title}</h3>
                                <p style={{ fontSize: "13.5px", color: "#9ca3af", lineHeight: 1.6, margin: 0 }}>{step.body}</p>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

function FeaturesSection() {
    const features = [
        {
            icon: <CreditIcon />,
            title: "Automatic credit issuance",
            body: "Set your cashback rules once. Pabloo issues credit after every qualifying order without you needing to do anything manually.",
        },
        {
            icon: <BellIcon />,
            title: "Credit balance email notifications",
            body: "Customers are reminded they have credit waiting. These notifications pull them back at the point they're most likely to place another order.",
        },
        {
            icon: <StarIcon />,
            title: "Loyalty tiers",
            body: "Reward your best customers with higher cashback rates as they spend more. Customers in higher tiers buy more frequently and churn less.",
        },
        {
            icon: <RefreshIcon />,
            title: "Cashback on specific products",
            body: "Attach higher credit bonuses to slow-moving inventory, new product launches, or any SKU you want to move faster.",
        },
        {
            icon: <RetainIcon />,
            title: "Customer credit account dashboard",
            body: "Every customer can see their credit balance, history, and pending rewards. Transparency drives trust and repeat purchases.",
        },
        {
            icon: <TrendIcon />,
            title: "Analytics and reporting",
            body: "Track repeat purchase rate, credit redemption rate, and which cashback rules are driving the most return visits.",
        },
    ];
    return (
        <section style={{ background: "#0a0a0a", padding: "80px 24px" }}>
            <div style={{ maxWidth: "960px", margin: "0 auto" }}>
                <div style={{ textAlign: "center", marginBottom: "56px" }}>
                    <p style={{ fontSize: "11px", fontWeight: 700, color: "#22c55e", textTransform: "uppercase", letterSpacing: "0.12em", marginBottom: "10px" }}>
                        Features
                    </p>
                    <h2 style={{ fontSize: "clamp(24px, 4vw, 36px)", fontWeight: 800, color: "#fff", lineHeight: 1.2 }}>
                        Everything you need to run a cashback program
                    </h2>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: "20px" }}>
                    {features.map((f) => (
                        <div key={f.title} style={{ background: "#111", border: "1px solid #1e1e1e", borderRadius: "16px", padding: "28px" }}>
                            <div style={{ marginBottom: "16px" }}>{f.icon}</div>
                            <h3 style={{ fontSize: "15px", fontWeight: 700, color: "#fff", marginBottom: "8px" }}>{f.title}</h3>
                            <p style={{ fontSize: "13.5px", color: "#9ca3af", lineHeight: 1.65, margin: 0 }}>{f.body}</p>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

function CashbackVsDiscountSection() {
    const rows = [
        { label: "Full-price margin preserved on every order", cashback: true, discount: false },
        { label: "Brings customers back for a second order", cashback: true, discount: false },
        { label: "No coupon leaking to third-party sites", cashback: true, discount: false },
        { label: "Increases perceived value of the brand", cashback: true, discount: false },
        { label: "Filters in repeat buyers, not deal-hunters", cashback: true, discount: false },
        { label: "Runs automatically without manual work", cashback: true, discount: false },
    ];
    return (
        <section style={{ background: "#050505", padding: "80px 24px" }}>
            <div style={{ maxWidth: "820px", margin: "0 auto" }}>
                <div style={{ textAlign: "center", marginBottom: "48px" }}>
                    <p style={{ fontSize: "11px", fontWeight: 700, color: "#22c55e", textTransform: "uppercase", letterSpacing: "0.12em", marginBottom: "10px" }}>
                        Cashback vs Discounts
                    </p>
                    <h2 style={{ fontSize: "clamp(24px, 4vw, 36px)", fontWeight: 800, color: "#fff", lineHeight: 1.2 }}>
                        Why cashback outperforms discount codes for retention
                    </h2>
                </div>
                <div style={{ background: "#111", border: "1px solid #1e1e1e", borderRadius: "16px", overflow: "hidden" }}>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 160px 160px", borderBottom: "1px solid #1e1e1e", padding: "14px 24px" }}>
                        <div />
                        <div style={{ fontSize: "12px", fontWeight: 700, color: "#22c55e", textAlign: "center" }}>Cashback</div>
                        <div style={{ fontSize: "12px", fontWeight: 700, color: "#9ca3af", textAlign: "center" }}>Discount codes</div>
                    </div>
                    {rows.map((row, i) => (
                        <div
                            key={row.label}
                            style={{
                                display: "grid",
                                gridTemplateColumns: "1fr 160px 160px",
                                padding: "14px 24px",
                                borderBottom: i < rows.length - 1 ? "1px solid #1a1a1a" : "none",
                                alignItems: "center",
                            }}
                        >
                            <div style={{ fontSize: "13.5px", color: "#d1d5db" }}>{row.label}</div>
                            <div style={{ display: "flex", justifyContent: "center" }}>
                                {row.cashback
                                    ? <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                                    : <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                                }
                            </div>
                            <div style={{ display: "flex", justifyContent: "center" }}>
                                {row.discount
                                    ? <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                                    : <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                                }
                            </div>
                        </div>
                    ))}
                </div>
                <div style={{ textAlign: "center", marginTop: "20px" }}>
                    <a href="/solutions/shopify/cashback/shopify-cashback-vs-discounts" style={{ fontSize: "13px", color: "#22c55e", textDecoration: "none" }}>
                        Full comparison: cashback vs discounts
                    </a>
                </div>
            </div>
        </section>
    );
}

function RelatedSolutionsSection() {
    const solutions = [
        {
            title: "Cart abandonment recovery",
            body: "Offer store credit to customers who leave items in their cart. Brings back shoppers who were on the fence without giving away a discount.",
            href: "/solutions/shopify-cart-abandonment-solutions",
        },
        {
            title: "Chargeback prevention",
            body: "Issue credit when a customer raises a dispute. Most customers withdraw the bank claim when offered a fair resolution they can use immediately.",
            href: "/solutions/shopify/no-more-chargebacks",
        },
        {
            title: "Inventory liquidation",
            body: "Attach bonus store credit to slow-moving products. Moves old inventory at full price and generates a return visit when the credit is spent.",
            href: "/solutions/shopify/inventory-liquidation",
        },
    ];
    return (
        <section style={{ background: "#0a0a0a", padding: "80px 24px" }}>
            <div style={{ maxWidth: "860px", margin: "0 auto" }}>
                <div style={{ textAlign: "center", marginBottom: "48px" }}>
                    <p style={{ fontSize: "11px", fontWeight: 700, color: "#22c55e", textTransform: "uppercase", letterSpacing: "0.12em", marginBottom: "10px" }}>
                        More Solutions
                    </p>
                    <h2 style={{ fontSize: "clamp(24px, 4vw, 36px)", fontWeight: 800, color: "#fff", lineHeight: 1.2 }}>
                        Other ways Pabloo uses store credit
                    </h2>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: "16px" }}>
                    {solutions.map((s) => (
                        <a
                            key={s.title}
                            href={s.href}
                            style={{ background: "#111", border: "1px solid #1e1e1e", borderRadius: "14px", padding: "24px 28px", textDecoration: "none", display: "block" }}
                        >
                            <h3 style={{ fontSize: "15px", fontWeight: 700, color: "#fff", marginBottom: "10px" }}>{s.title}</h3>
                            <p style={{ fontSize: "13px", color: "#9ca3af", lineHeight: 1.6, margin: 0 }}>{s.body}</p>
                            <p style={{ fontSize: "12px", color: "#22c55e", marginTop: "14px", marginBottom: 0 }}>Learn more</p>
                        </a>
                    ))}
                </div>
            </div>
        </section>
    );
}

function FinalCTA({ onCTA }) {
    return (
        <section style={{ background: "#0a0a0a", borderTop: "1px solid #1a1a1a", padding: "80px 24px", textAlign: "center" }}>
            <div style={{ maxWidth: "560px", margin: "0 auto" }}>
                <h2 style={{ fontSize: "clamp(24px, 4vw, 36px)", fontWeight: 800, color: "#fff", lineHeight: 1.2, marginBottom: "16px" }}>
                    Start turning one-time buyers into regulars
                </h2>
                <p style={{ fontSize: "15px", color: "#9ca3af", lineHeight: 1.6, marginBottom: "32px" }}>
                    14-day free trial. Installs on Shopify in minutes. No credit card required to start.
                </p>
                <button
                    onClick={onCTA}
                    style={{ padding: "15px 32px", background: "#22c55e", color: "#fff", border: "none", borderRadius: "10px", fontSize: "15px", fontWeight: 700, cursor: "pointer" }}
                >
                    Start free trial
                </button>
            </div>
        </section>
    );
}

// ─── Main component ───────────────────────────────────────────────────────────

const SolutionComponent = () => {
    const navigate = useNavigate();

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: "auto" });
        sendSlackMessageTracked("Lead is visiting the Cashback Solutions page.", "#website-updates");
    }, []);

    const handleCTA = () => { window.location.href = "https://app.pabloo.com/sign-up"; };

    return (
        <div style={{ background: "#050505" }}>
            <Hero onCTA={handleCTA} />
            <StatsStrip />
            <ProblemsSection />
            <HowCashbackWorksSection />
            <FeaturesSection />
            <CashbackVsDiscountSection />
            <RelatedSolutionsSection />
            <ModernPricingView />
            <FinalCTA onCTA={handleCTA} />
        </div>
    );
};

export default SolutionComponent;
